// components/checklist/UserCreatedChecklistsInProject.tsx
import { Alert, Box, Button, Progress, Flex } from "@mantine/core";
import "ag-grid-enterprise";
import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridOptions } from "ag-grid-community";

import { IChecklistProgress } from "@mctypes/checklist.types";
import { useAppSelector } from "../../../../hooks";
import { DisplayDueDate, FailedAlert, MCLoader, McInputNew, HSpace } from "../../../../components";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { STANDARD_COLORS } from "../../../globalConstants";
import { StatusBadgeRenderer } from "../../checklist/ChecklistColums";
import { useGetChecklistCreatedByUserQuery, useGetProjectByProjectIdChecklistCreatedByUserQuery } from "../../../../../openapi";
import type { GetProjectByProjectIdChecklistCreatedByUserApiResponse } from "../../../../../openapi";
import { normalizeId } from "@mcapps/shared/sharedUtils";
import "../../checklist/checklistapi.enhance";
import { CHECKLIST_STATES_OPTIONS, CHECKLIST_STATES_OPTIONS_COLOR } from "../../checklist/constants";
import { isValidDateValue } from "../../admin/selectors";

// ----- typer -----
type Row = GetProjectByProjectIdChecklistCreatedByUserApiResponse[number];

// ----- kolumner -----
const createColumns = (goToChecklist: (row: Row) => void): ColDef<Row>[] => [
  {
    field: "status",
    headerName: "Status",
    width: 160,
    editable: false,
    filter: true,
    valueGetter: (p) => {
      const base = CHECKLIST_STATES_OPTIONS.find((opt) => opt.value === p.data.status);
      const colored = CHECKLIST_STATES_OPTIONS_COLOR.find((opt) => opt.value === base?.displayValue);
      return colored ?? base ?? p.data.status;
    },
    cellRenderer: StatusBadgeRenderer,
  },
  {
    field: "name",
    headerName: "Sjekklister",
    minWidth: 220,
    flex: 2,
    editable: false,
    filter: "agTextColumnFilter",
    cellRenderer: (p) => (
      <Flex style={{ alignItems: "center" }}>
        <Button m={0} p={0} onClick={() => goToChecklist(p.data)} variant="subtle">
          {p.value}
        </Button>
        <HSpace />
      </Flex>
    ),
  },
  {
    field: "progress",
    headerName: "Fremskritt",
    minWidth: 180,
    editable: false,
    sortable: false,
    filter: false,
    cellRenderer: (p) => {
      const progress = p.value as IChecklistProgress | undefined;
      if (!progress) return "-";
      const accepted = Math.max(0, Math.min(100, Number(progress.accepted ?? 0)));
      const rejected = Math.max(0, Math.min(100 - accepted, Number(progress.rejected ?? 0)));
      return (
        <Box w="100%">
          <Progress.Root size={12} radius={4}>
            <Progress.Section value={accepted} color={STANDARD_COLORS.green} />
            <Progress.Section value={rejected} color={STANDARD_COLORS.red} />
          </Progress.Root>
        </Box>
      );
    },
  },
  {
    field: "dueDate",
    headerName: "Frist",
    width: 160,
    editable: false,
    filter: "agDateColumnFilter",
    cellRenderer: (p) => {
      const v = p.value as string | undefined;
      if (isValidDateValue(v) === false) return "-";
      return <DisplayDueDate dueDate={v} isComplete={p.data.status === "DELIVERED"} />;
    },
  },
];

const UserCreatedChecklistsInProject: React.FC = () => {
  const projectId = useAppSelector((s) => s.globalReducer.currentProjectId); // kan være undefined

  const history = useHistory();
  const gridRef = useRef<AgGridReact<Row>>(null);
  const [search, setSearch] = useState("");

  const projectChecklists = useGetProjectByProjectIdChecklistCreatedByUserQuery(
    { projectId: projectId ?? "" },
    { skip: projectId == null },
  );
  const allProjectChecklists = useGetChecklistCreatedByUserQuery(undefined, { skip: projectId != null });
  const { data = [], isLoading, isFetching, error } = projectId == null ? allProjectChecklists : projectChecklists;

  const rows: Row[] = data ?? [];

  const goToChecklist = useCallback(
    (checklist: Row) => {
      if (checklist) {
        history.push(`/project/${normalizeId(checklist.project)}/checklist/instance/${checklist._id}`);
      }
    },
    [history],
  );

  const columns = useMemo(() => createColumns(goToChecklist), [goToChecklist]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 140,
      suppressHeaderMenuButton: true,
      autoHeight: true,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5", wordBreak: "break-word" },
    }),
    [],
  );

  const getRowId = useCallback((p: { data: Row }) => p.data._id, []);

  const gridOptions = useMemo<GridOptions<Row>>(
    () => ({
      maintainColumnOrder: true,
      suppressColumnMoveAnimation: true,
    }),
    [],
  );

  // Vis gridens overlay mens data refetches (behold radene synlige)
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (isFetching) api.showLoadingOverlay();
    else api.hideOverlay();
  }, [isFetching]);

  return (
    <>
      {/* Søk */}
      <Box pt="0.5rem" pb="0.25rem">
        <McInputNew
          placeholder="Søk…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
      </Box>

      {isLoading && <MCLoader loading />}
      {!!error && <FailedAlert errorMessage="Kunne ikke laste inn dine sjekklister" />}

      {!error && !isLoading && rows.length === 0 && (
        <Box flex={1} px="0.25rem" style={{ overflow: "auto" }}>
          <Alert color="green" variant="light" mt="xl">
            <i className="fa fa-check fa-fw" />
            Du har ingen sjekklister{projectId ? " i dette prosjektet" : ""}.{" "}
            {projectId ? (
              <Link to={`/project/${projectId}/checklist/instance`}>Gå til sjekklisteappen</Link>
            ) : (
              <Link to={`/checklist/instance`}>Gå til sjekklisteappen</Link>
            )}{" "}
            for å se alle sjekklister.
          </Alert>
        </Box>
      )}

      {!error && (
        <Box className="ag-theme-quartz" h="100%" w="100%">
          <AgGridReact<Row>
            ref={gridRef}
            rowData={rows}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
            getRowId={getRowId}
            suppressHeaderFocus
            cellSelection
            quickFilterText={search}
            localeText={AG_GRID_LOCALE_NOR}
          />
        </Box>
      )}
    </>
  );
};

export default UserCreatedChecklistsInProject;
